import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, patch, del } from '@/lib/api'

// ==================== Types ====================

export interface Comment {
  id: string
  issueId: string
  authorId: string
  authorName: string | null
  content: string
  mentions: string | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface CommentListParams {
  page?: number
  pageSize?: number
}

export interface CreateCommentInput {
  content: string
}

export interface UpdateCommentInput {
  content: string
}

// ==================== Query Keys ====================

export const commentKeys = {
  all: ['comments'] as const,
  lists: () => [...commentKeys.all, 'list'] as const,
  list: (issueId: string, params: CommentListParams) => [...commentKeys.lists(), issueId, params] as const,
  details: () => [...commentKeys.all, 'detail'] as const,
  detail: (id: string) => [...commentKeys.details(), id] as const,
}

// ==================== Hooks ====================

/**
 * 获取任务评论列表
 */
export function useComments(issueId: string, params: CommentListParams = {}) {
  return useQuery({
    queryKey: commentKeys.list(issueId, params),
    queryFn: () => get<Comment[]>(`/issues/${issueId}/comments`, params as Record<string, unknown>),
    enabled: !!issueId,
  })
}

/**
 * 获取单个评论
 */
export function useComment(id: string) {
  return useQuery({
    queryKey: commentKeys.detail(id),
    queryFn: () => get<Comment>(`/comments/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建评论
 */
export function useCreateComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: CreateCommentInput }) =>
      post<Comment>(`/issues/${issueId}/comments`, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['issues', variables.issueId] })
    },
  })
}

/**
 * 更新评论
 */
export function useUpdateComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCommentInput }) =>
      patch<Comment>(`/comments/${id}`, data),
    onSuccess: (comment) => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.detail(comment.id) })
      void queryClient.invalidateQueries({ queryKey: commentKeys.lists() })
    },
  })
}

/**
 * 删除评论
 */
export function useDeleteComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/comments/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.lists() })
    },
  })
}

// ==================== Utilities ====================

/**
 * 解析评论中的 @提及
 * 格式: @[用户名](用户ID)
 */
export function parseMentions(content: string): Array<{ name: string; id: string }> {
  const regex = /@\[([^\]]+)\]\(([^)]+)\)/g
  const mentions: Array<{ name: string; id: string }> = []
  let match

  while ((match = regex.exec(content)) !== null) {
    mentions.push({ name: match[1], id: match[2] })
  }

  return mentions
}

/**
 * 将评论内容中的 @提及转换为高亮显示的 HTML
 */
export function highlightMentions(content: string): string {
  return content.replace(
    /@\[([^\]]+)\]\(([^)]+)\)/g,
    '<span class="text-primary font-medium">@$1</span>'
  )
}
